import {isPlatformBrowser} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom, of} from 'rxjs';
import * as L from 'leaflet';
import {
  CertificateItemDto,
  TradeAllocationDto,
  TradeChainDto,
  TradeRecordDto,
  TradeRecordKind,
  TradeRecordLogDto
} from '../../../../models/generated-models';
import {UserCertificateApiService} from '../../user-certificate/user-certificate-api.service';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {RecordCodeComponent} from '../../../shared/commons/trade/record-code.component';
import {normalizeUserRoles, STAFF_ROLES} from '../../../app.constants';
import {AuthService} from '../../../shared/services/auth.service';
import {TradeApiService} from '../trade-api.service';
import {DEFAULT_TRADE_UNIT, TRADE_BASE, tradeKindBadgeClass, tradeKindLabel, tradeRecordLabel} from '../trade.constants';
import {formatQuantityWithUnit} from '../trade-utils';
import {TradeJourneyGraphComponent} from './trade-journey-graph.component';
import {JOURNEY_COLORS, JOURNEY_CURRENT_COLOR} from './trade-journey-sankey';
import {buildJourneyTree, ChainTreeNode, ChainTreePins} from './trade-chain-tree';
import {TradeChainTreeComponent} from './trade-chain-tree.component';

export interface AncestorLevel {
  depth: number;
  records: TradeRecordDto[];
}

/**
 * One pin of the route map (work item 184): every record of the journey that sits at the same
 * coordinates shares one pin (three sales to the same buyer are one pin, not three stacked ones),
 * and a ด่านส่งออก is a pin of its own.
 */
export interface MapNode {
  key: string;
  latitude: number;
  longitude: number;
  title: string;
  subtitle: string;
  /** `CHECKPOINT` is the ด่านส่งออก pin that closes an export chain. */
  kind: TradeRecordKind | 'CHECKPOINT';
  /** Holds the record being viewed. */
  current: boolean;
  records: TradeRecordDto[];
  /** 1-based position in flow order (sources before the records that drew from them). */
  order: number;
}

/**
 * One arrow of the route map: goods went from {@code from} to {@code to}. Built from the
 * allocation lines of the journey (merged when several lines join the same two pins), plus the
 * leg from an export record to its ด่าน — never from the order records happen to be listed in.
 */
export interface MapEdge {
  from: MapNode;
  to: MapNode;
  quantity: number;
  /** Allocation lines merged into this arrow. */
  count: number;
  /** Colour: the side that received the goods. */
  kind: TradeRecordKind | 'CHECKPOINT';
  /** Leaves or enters the record being viewed. */
  current: boolean;
}

export interface MapGraph {
  nodes: MapNode[];
  edges: MapEdge[];
}

/** A record that drew from this one, merged with the allocation line that fed it. */
export interface DownstreamItem {
  recordId?: number;
  recordCode?: string;
  kind?: TradeRecordKind;
  businessTitle: string;
  roleLabel: string;
  quantity?: number;
  unit?: string;
  recordedAt?: Date;
  productName?: string;
  /** Legacy SHIPMENT rows only — no new record carries a destination. */
  destination?: string;
  truckNo?: string;
}

/** Thailand — the view when no hop of the chain carries coordinates. */
const THAILAND_CENTER: L.LatLngTuple = [13.75, 100.5];
const THAILAND_ZOOM = 6;

/** Marker/route colours, carried over from the approved farmer record page and shared with the graph. */
const KIND_COLORS: Record<string, string> = JOURNEY_COLORS;
const CURRENT_COLOR = JOURNEY_CURRENT_COLOR;

/** What the right-hand pane shows: the journey Sankey (default) or the Leaflet map. */
export type JourneyView = 'graph' | 'map';

@Component({
  selector: 'app-trade-detail',
  standalone: true,
  imports: [RouterLink, ThaiDateTimePipe, RecordCodeComponent, TradeJourneyGraphComponent, TradeChainTreeComponent],
  templateUrl: './trade-detail.component.html',
  styleUrl: './trade-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeDetailComponent implements OnInit, OnDestroy {
  private readonly api = inject(TradeApiService);
  private readonly certificateApi = inject(UserCertificateApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);

  /**
   * Only officers get the observations on the graph — business users never see them. Staff carry
   * their roles as token authorities (the profile's userRoles is empty for them), so this reads the
   * same effective set the sidebar menu uses.
   */
  protected readonly showObservations = computed<boolean>(() => {
    const roles = normalizeUserRoles(this.authService.effectiveRoles());
    return STAFF_ROLES.some((role) => roles.includes(role));
  });

  protected readonly recordId = signal<number | null>(null);
  protected readonly chain = signal<TradeChainDto | null>(null);
  protected readonly loading = signal(false);
  protected readonly notFound = signal(false);
  protected readonly uploading = signal(false);
  /** Audit trail (who created / edited / deleted what), newest first. */
  protected readonly history = signal<TradeRecordLogDto[]>([]);

  /** Panel/map split of the old page: desktop splits, mobile opens as an expanded sheet. */
  protected readonly sheetMode = signal<'split' | 'minimized' | 'expanded'>('split');
  protected readonly activePanelTab = signal<'details' | 'chain'>('details');
  /** The map is the default view (work item 171); the journey graph is one switch away. */
  protected readonly viewMode = signal<JourneyView>('map');

  protected readonly record = computed<TradeRecordDto | null>(() => this.chain()?.record ?? null);

  /**
   * ประเภทธุรกิจ as the record stored them when it was written — the snapshot on the
   * record, never the live business profile, so editing the profile later leaves old records alone.
   */
  protected readonly businessTypeTitles = computed<string[]>(() => {
    const titles = (this.record()?.businessTypeTitles ?? [])
      .map((title) => `${title ?? ''}`.trim())
      .filter((title) => title.length > 0);
    return Array.from(new Set(titles));
  });

  /** The signed-in user's certificates, used only to look up standard codes by certificate number. */
  private readonly myCertificates = signal<CertificateItemDto[]>([]);

  private map?: L.Map;
  private readonly mapLayers: L.Layer[] = [];

  /**
   * The canvas only exists once the chain has loaded and the map view is on, so the
   * view-child setter is where the map is created — zoneless-safe, and what the old page
   * did — and where it is torn down again when the graph takes the pane over.
   */
  @ViewChild('mapContainer')
  private set mapContainer(container: ElementRef<HTMLDivElement> | undefined) {
    if (container) {
      this.initializeMap(container.nativeElement);
    } else {
      this.destroyMap();
    }
  }

  constructor() {
    // Leaflet is imperative: redraw the route whenever the chain signal changes.
    effect(() => {
      this.chain();
      this.renderMapStops();
    });
  }

  /**
   * มาตรฐาน / ชื่อมาตรฐาน / เลขที่ใบรับรอง per certificate on the record. The record stores the
   * standard name and the number; the code comes from the viewer's own certificate list when it
   * has that number (owners always do), otherwise it is shown as "—".
   */
  protected readonly standards = computed(() => {
    const current = this.record();
    if (!current) {
      return [];
    }
    const nos = current.certificateNos ?? [];
    const names = current.standardList ?? [];
    const count = Math.max(nos.length, names.length);
    const byNo = new Map<string, CertificateItemDto>();
    for (const cert of this.myCertificates()) {
      const key = `${cert.certificateNo ?? ''}`.replace(/\s+/g, '').toUpperCase();
      if (key) {
        byNo.set(key, cert);
      }
    }
    const codes = current.standardCodes ?? [];
    const rows: {standardCode: string; standardName: string; certificateNo: string}[] = [];
    for (let i = 0; i < count; i++) {
      const certificateNo = nos[i] ?? '';
      const match = byNo.get(certificateNo.replace(/\s+/g, '').toUpperCase());
      // The backend resolves the code from whoever holds the certificate; the viewer's own
      // certificates are only a fallback for older payloads.
      const code = `${codes[i] ?? match?.standardCode ?? ''}`.trim();
      rows.push({
        standardCode: code && code !== '-' ? code : '',
        standardName: names[i] ?? match?.standardName ?? '',
        certificateNo
      });
    }
    return rows;
  });

  /**
   * A pooled record can be fed by several lots, so the upstream side is not a
   * single line: ancestors are grouped by depth, root level first.
   */
  protected readonly ancestorLevels = computed<AncestorLevel[]>(() => {
    const ancestors = this.chain()?.ancestors ?? [];
    const byDepth = new Map<number, TradeRecordDto[]>();
    for (const item of ancestors) {
      const depth = item.depth ?? 0;
      byDepth.set(depth, [...(byDepth.get(depth) ?? []), item]);
    }
    return [...byDepth.entries()]
      .map(([depth, records]) => ({depth, records}))
      .sort((left, right) => left.depth - right.depth);
  });

  /** Allocation lines this record drew from (pooled stock may span several lots). */
  protected readonly sources = computed<TradeAllocationDto[]>(() => this.record()?.sources ?? []);

  /** Allocation lines other records drew from this one. */
  protected readonly outgoing = computed<TradeAllocationDto[]>(() => this.chain()?.outgoing ?? []);

  protected readonly children = computed<TradeRecordDto[]>(() => this.chain()?.children ?? []);

  /**
   * The downstream summary the old page showed as "พ่อค้าคนกลาง / ส่งออก": one card per
   * record that drew from this one. `children` carries the record, `outgoing` the quantity
   * actually taken, so the two are merged by record id.
   */
  protected readonly downstream = computed<DownstreamItem[]>(() => {
    const lines = this.outgoing();
    const takenById = new Map<number, number>();
    for (const line of lines) {
      if (line.recordId != null) {
        takenById.set(line.recordId, (takenById.get(line.recordId) ?? 0) + (line.quantity ?? 0));
      }
    }
    const fallbackUnit = this.record()?.unit || this.unit;
    const items: DownstreamItem[] = this.children().map((child) => ({
      recordId: child.id,
      recordCode: child.recordCode,
      kind: child.kind,
      businessTitle: child.businessTitle || '-',
      roleLabel: child.kind === 'SHIPMENT' ? 'ผู้ขนส่ง' : 'ผู้ซื้อ',
      quantity: child.id != null && takenById.has(child.id) ? takenById.get(child.id) : child.quantity,
      unit: child.unit || fallbackUnit,
      recordedAt: child.recordedAt,
      productName: child.productName,
      destination: child.destination,
      truckNo: child.truckNo
    }));
    // A line whose record is not in `children` (it may belong to another owner) still counts.
    const known = new Set(items.map((item) => item.recordId).filter((id): id is number => id != null));
    for (const line of lines) {
      if (line.recordId != null && known.has(line.recordId)) {
        continue;
      }
      items.push({
        recordId: line.recordId,
        kind: line.recordKind,
        businessTitle: line.recordBusinessTitle || '-',
        roleLabel: line.recordKind === 'SHIPMENT' ? 'ผู้ขนส่ง' : 'ผู้ซื้อ',
        quantity: line.quantity,
        unit: fallbackUnit,
        recordedAt: line.recordRecordedAt
      });
    }
    return items;
  });

  /**
   * Every record of the journey, the same set the Sankey draws: ancestors (roots first), this
   * record, everything downstream and the lots it was pooled with.
   */
  protected readonly journeyRecords = computed<TradeRecordDto[]>(() => {
    const chain = this.chain();
    const current = chain?.record;
    if (!chain || !current) {
      return [];
    }
    const seen = new Set<number>();
    const records: TradeRecordDto[] = [];
    for (const item of [
      ...this.ancestorLevels().flatMap((level) => level.records),
      current,
      ...(chain.descendants ?? chain.children ?? []),
      ...(chain.related ?? [])
    ]) {
      if (item.id != null && seen.has(item.id)) {
        continue;
      }
      if (item.id != null) {
        seen.add(item.id);
      }
      records.push(item);
    }
    return records;
  });

  /**
   * The route as a graph (work item 184). Pins are locations, arrows are the allocation lines
   * between them, and the pin numbers follow the flow (a source before whatever drew from it),
   * so a lot sold to three buyers fans out instead of being joined buyer-to-buyer.
   */
  protected readonly mapGraph = computed<MapGraph>(() => {
    const current = this.record();
    const chain = this.chain();
    const nodes = new Map<string, MapNode>();
    const nodeOfRecord = new Map<number, MapNode>();

    for (const item of this.journeyRecords()) {
      const latitude = this.parseCoordinate(item.lat, 90);
      const longitude = this.parseCoordinate(item.lon, 180);
      if (latitude == null || longitude == null) {
        continue;
      }
      const key = `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
      const isCurrent = current != null && item.id === current.id;
      let node = nodes.get(key);
      if (!node) {
        node = {
          key,
          latitude,
          longitude,
          title: item.businessTitle || item.farmName || tradeRecordLabel(item),
          subtitle: '',
          kind: item.kind ?? 'HARVEST',
          current: false,
          records: [],
          order: 0
        };
        nodes.set(key, node);
      }
      node.records.push(item);
      if (isCurrent) {
        // The record being viewed names the pin, whatever else sits there.
        node.current = true;
        node.kind = item.kind ?? node.kind;
        node.title = item.businessTitle || item.farmName || node.title;
      }
      if (item.id != null) {
        nodeOfRecord.set(item.id, node);
      }
    }
    for (const node of nodes.values()) {
      node.subtitle = this.nodeSubtitle(node);
    }

    const edges = new Map<string, MapEdge>();
    const join = (from: MapNode | undefined, to: MapNode | undefined, quantity: number,
                  kind: TradeRecordKind | 'CHECKPOINT', touchesCurrent: boolean): void => {
      // Two records on the same pin (a transform inside one business) have no arrow to draw.
      if (!from || !to || from === to) {
        return;
      }
      const key = `${from.key}>${to.key}`;
      const existing = edges.get(key);
      if (existing) {
        existing.quantity += quantity;
        existing.count += 1;
        existing.current = existing.current || touchesCurrent;
        return;
      }
      edges.set(key, {from, to, quantity, count: 1, kind, current: touchesCurrent});
    };

    const lines: TradeAllocationDto[] = chain?.links ?? [
      // Older payloads carry only the record's own lines and the ones out of it.
      ...(current?.sources ?? []),
      ...(chain?.outgoing ?? [])
    ];
    for (const line of lines) {
      if (line.sourceRecordId == null || line.recordId == null) {
        continue;
      }
      const to = nodeOfRecord.get(line.recordId);
      const kind = line.recordKind
        ?? to?.records.find((item) => item.id === line.recordId)?.kind
        ?? 'PURCHASE';
      join(nodeOfRecord.get(line.sourceRecordId), to, Math.max(0, line.quantity ?? 0), kind,
        current != null && (line.sourceRecordId === current.id || line.recordId === current.id));
    }

    // An export record's goods leave through its ด่าน: one more pin, one more arrow.
    for (const item of this.journeyRecords()) {
      const checkpoint = this.checkpointNode(item);
      if (!checkpoint || item.id == null) {
        continue;
      }
      let node = nodes.get(checkpoint.key);
      if (!node) {
        node = checkpoint;
        nodes.set(node.key, node);
      }
      node.records.push(item);
      join(nodeOfRecord.get(item.id), node, Math.max(0, item.quantity ?? 0), 'CHECKPOINT',
        current != null && item.id === current.id);
    }

    const ordered = this.flowOrder([...nodes.values()], [...edges.values()]);
    ordered.forEach((node, index) => node.order = index + 1);
    return {nodes: ordered, edges: [...edges.values()]};
  });

  /** "ผลผลิต · ลำไย" for a single record, "3 รายการ · ซื้อ, ผลผลิต" for a shared pin. */
  private nodeSubtitle(node: MapNode): string {
    if (node.kind === 'CHECKPOINT') {
      return 'ด่านที่ส่งออก · สิ้นสุดห่วงโซ่';
    }
    if (node.records.length === 1) {
      const only = node.records[0];
      return `${tradeRecordLabel(only)} · ${only.productName ?? ''}`.trim();
    }
    const kinds = Array.from(new Set(node.records.map((item) => tradeRecordLabel(item))));
    return `${node.records.length} รายการ · ${kinds.join(', ')}`;
  }

  /** The ด่านส่งออก pin of an export record, when the checkpoint has coordinates. */
  private checkpointNode(record: TradeRecordDto): MapNode | null {
    if (record.export !== true) {
      return null;
    }
    const latitude = this.parseCoordinate(record.exportCheckpointLat, 90);
    const longitude = this.parseCoordinate(record.exportCheckpointLon, 180);
    if (latitude == null || longitude == null) {
      return null;
    }
    return {
      key: `C${record.exportCheckpointId ?? `${latitude.toFixed(5)},${longitude.toFixed(5)}`}`,
      latitude,
      longitude,
      title: record.exportCheckpointName || 'ด่านที่ส่งออก',
      subtitle: 'ด่านที่ส่งออก · สิ้นสุดห่วงโซ่',
      kind: 'CHECKPOINT',
      current: false,
      records: [],
      order: 0
    };
  }

  /**
   * Pins in flow order: a pin comes after every pin that feeds it (Kahn's algorithm, ties kept in
   * journey order — ancestors first). A pin nothing points at or from keeps its journey position.
   */
  private flowOrder(nodes: MapNode[], edges: MapEdge[]): MapNode[] {
    const indegree = new Map<MapNode, number>(nodes.map((node) => [node, 0]));
    const outgoing = new Map<MapNode, MapNode[]>(nodes.map((node) => [node, []]));
    for (const edge of edges) {
      indegree.set(edge.to, (indegree.get(edge.to) ?? 0) + 1);
      outgoing.get(edge.from)?.push(edge.to);
    }
    const ordered: MapNode[] = [];
    const ready = nodes.filter((node) => (indegree.get(node) ?? 0) === 0);
    while (ready.length > 0) {
      const node = ready.shift()!;
      ordered.push(node);
      for (const next of outgoing.get(node) ?? []) {
        const left = (indegree.get(next) ?? 0) - 1;
        indegree.set(next, left);
        if (left === 0) {
          // Keep journey order among the pins that become ready together.
          const at = ready.findIndex((waiting) => nodes.indexOf(waiting) > nodes.indexOf(next));
          ready.splice(at < 0 ? ready.length : at, 0, next);
        }
      }
    }
    // A cycle cannot happen on a chain, but never drop a pin if the data is odd.
    for (const node of nodes) {
      if (!ordered.includes(node)) {
        ordered.push(node);
      }
    }
    return ordered;
  }

  protected readonly hasCoordinates = computed(() => this.mapGraph().nodes.length > 0);

  /** The ห่วงโซ่ tab: the whole journey as one tree, origin lots first, this record marked in place. */
  protected readonly journeyTree = computed<ChainTreeNode[]>(() => buildJourneyTree(this.chain()));

  /**
   * The map pin number of each record (and of each export record's ด่าน), so the tree carries the
   * same numbers as the map — a record that pooled two lots appears twice in the tree with the
   * same number, which is how the reader sees it is one record, not two.
   */
  protected readonly pinNumbers = computed<ChainTreePins>(() => {
    const records = new Map<number, number>();
    const checkpoints = new Map<number, number>();
    for (const node of this.mapGraph().nodes) {
      for (const item of node.records) {
        if (item.id == null) {
          continue;
        }
        (node.kind === 'CHECKPOINT' ? checkpoints : records).set(item.id, node.order);
      }
    }
    return {records, checkpoints};
  });

  /** Businesses along the route that have no location yet — their pins cannot be drawn. */
  protected readonly businessesWithoutLocation = computed<string[]>(() => {
    const names: string[] = [];
    for (const item of this.journeyRecords()) {
      const latitude = this.parseCoordinate(item.lat, 90);
      const longitude = this.parseCoordinate(item.lon, 180);
      if (latitude != null && longitude != null) {
        continue;
      }
      const name = (item.businessTitle || item.farmName || '').trim();
      if (name && !names.includes(name)) {
        names.push(name);
      }
    }
    return names;
  });

  /** Only the viewer's own businesses can be fixed by the viewer — never link into someone else's. */
  protected readonly canSetMissingLocation = computed<boolean>(() =>
    this.journeyRecords().some((item) => {
      const latitude = this.parseCoordinate(item.lat, 90);
      const longitude = this.parseCoordinate(item.lon, 180);
      return (latitude == null || longitude == null) && item.owned === true;
    })
  );

  protected readonly soldQty = computed(() => {
    const record = this.record();
    if (!record) {
      return 0;
    }
    return record.soldQty ?? Math.max(0, (record.quantity ?? 0) - (record.remainingQty ?? 0));
  });

  protected readonly auditActionLabels: Record<string, string> = {
    CREATE: 'สร้าง',
    UPDATE: 'แก้ไข',
    DELETE: 'ลบ'
  };
  protected readonly auditActionBadges: Record<string, string> = {
    CREATE: 'text-bg-success',
    UPDATE: 'text-bg-warning',
    DELETE: 'text-bg-danger'
  };

  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly tradeRecordLabel = tradeRecordLabel;
  protected readonly tradeKindBadgeClass = tradeKindBadgeClass;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;
  protected readonly tradeBase = TRADE_BASE;
  /** The only unit in the system — a fallback for older rows. */
  protected readonly unit = DEFAULT_TRADE_UNIT;

  ngOnInit(): void {
    this.sheetMode.set(this.isMobileViewport() ? 'expanded' : 'split');
    // Clicking a node of the graph navigates to another record while this component is
    // reused, so the id has to be read from the live params, not the snapshot.
    const params = this.route.paramMap ?? of(this.route.snapshot.paramMap);
    params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((map) => this.open(Number(map.get('id'))));
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  private open(id: number): void {
    if (!Number.isInteger(id)) {
      this.recordId.set(null);
      this.chain.set(null);
      this.notFound.set(true);
      return;
    }
    this.recordId.set(id);
    this.load(id);
  }

  protected setViewMode(mode: JourneyView): void {
    if (this.viewMode() === mode) {
      return;
    }
    this.viewMode.set(mode);
  }

  /** A node of the journey graph was clicked: show that record instead. */
  protected openRecord(id: number): void {
    if (id !== this.recordId()) {
      void this.router.navigate([TRADE_BASE, id]);
    }
  }

  protected reload(): void {
    const id = this.recordId();
    if (id != null) {
      this.load(id);
    }
  }

  protected selectPanelTab(tab: 'details' | 'chain'): void {
    this.activePanelTab.set(tab);
  }

  protected toggleRouteCard(): void {
    if (this.sheetMode() === 'minimized') {
      this.sheetMode.set(this.isMobileViewport() ? 'expanded' : 'split');
    } else {
      this.sheetMode.set('minimized');
    }
    this.invalidateMapSize();
  }

  protected toggleExpand(): void {
    this.sheetMode.set(this.sheetMode() === 'expanded' ? 'split' : 'expanded');
    this.invalidateMapSize();
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    this.map?.invalidateSize();
  }

  protected recenterMap(): void {
    const map = this.map;
    if (!map) {
      return;
    }
    const nodes = this.mapGraph().nodes;
    if (!nodes.length) {
      map.setView(THAILAND_CENTER, THAILAND_ZOOM);
      return;
    }
    map.fitBounds(this.routeBounds(nodes), {
      padding: [48, 48],
      maxZoom: nodes.length === 1 ? 12 : 19
    });
  }


  protected onCoverImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const id = this.recordId();
    if (!file || id == null) {
      return;
    }
    this.uploading.set(true);
    this.api.uploadCoverImage(id, file).subscribe({
      next: () => {
        input.value = '';
        this.uploading.set(false);
        this.load(id);
      },
      error: () => {
        input.value = '';
        this.uploading.set(false);
      }
    });
  }

  protected editRecord(): void {
    const id = this.recordId();
    if (id != null) {
      void this.router.navigate([TRADE_BASE, id, 'edit']);
    }
  }

  protected deleteRecord(): void {
    const id = this.recordId();
    if (id == null) {
      return;
    }
    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการลบ',
      content: 'ต้องการลบรายการนี้หรือไม่ ระบบจะคืนสต๊อกให้รายการต้นทาง',
      confirmButtonMsg: 'ลบรายการ',
      closeButtonMsg: 'ยกเลิก'
    })).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.api.deleteRecord(id).subscribe({
        next: () => void this.router.navigate([TRADE_BASE]),
        error: () => undefined
      });
    }).catch(() => undefined);
  }

  private load(id: number): void {
    this.loading.set(true);
    this.notFound.set(false);
    this.history.set([]);
    this.api.getHistory(id).subscribe({
      next: (entries) => this.history.set(entries ?? []),
      error: () => this.history.set([])
    });
    this.api.getChain(id).subscribe({
      next: (chain) => {
        this.chain.set(chain);
        if ((chain.record?.certificateNos?.length ?? 0) > 0 && this.myCertificates().length === 0) {
          this.certificateApi.listCertificates().subscribe({
            next: (items) => this.myCertificates.set(items ?? []),
            error: () => this.myCertificates.set([])
          });
        }
        this.notFound.set(!chain?.record);
        this.loading.set(false);
      },
      error: () => {
        this.chain.set(null);
        this.notFound.set(true);
        this.loading.set(false);
      }
    });
  }

  private isMobileViewport(): boolean {
    return isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && window.innerWidth <= 767.98;
  }

  private destroyMap(): void {
    this.map?.remove();
    this.map = undefined;
    this.mapLayers.length = 0;
  }

  private initializeMap(container: HTMLDivElement): void {
    if (!isPlatformBrowser(this.platformId) || this.map) {
      return;
    }
    this.map = L.map(container, {
      zoomControl: false,
      scrollWheelZoom: true,
      minZoom: 5,
      maxBoundsViscosity: 1
    }).setView(THAILAND_CENTER, THAILAND_ZOOM);
    L.control.zoom({position: 'bottomright'}).addTo(this.map);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(this.map);
    this.renderMapStops();
  }

  /** The pane is resized by the panel toggle, so Leaflet has to be told after the transition. */
  private invalidateMapSize(): void {
    if (!this.map) {
      return;
    }
    setTimeout(() => {
      this.map?.invalidateSize();
      this.recenterMap();
    }, 280);
  }

  /**
   * Pins numbered in flow order, joined by one dashed arrow per allocation (work item 184): the
   * arrow points the way the goods went, so the picture reads the same as the journey graph.
   */
  private renderMapStops(): void {
    const map = this.map;
    if (!map) {
      return;
    }
    for (const layer of this.mapLayers) {
      layer.remove();
    }
    this.mapLayers.length = 0;

    const {nodes, edges} = this.mapGraph();
    if (!nodes.length) {
      map.setView(THAILAND_CENTER, THAILAND_ZOOM);
      return;
    }
    // The legs of each pin, so a click on the pin can make them blink.
    const legsOfNode = new Map<MapNode, L.Polyline[]>();
    for (const edge of edges) {
      const segment = [L.latLng(edge.from.latitude, edge.from.longitude), L.latLng(edge.to.latitude, edge.to.longitude)];
      const color = edge.current ? CURRENT_COLOR : KIND_COLORS[edge.kind] ?? CURRENT_COLOR;
      // The current record's legs blink for a moment on every redraw, so after a jump to another
      // record (graph node, chip, link) the eye lands on the leg that belongs to it.
      const className = `route-leg${edge.current ? ' is-current is-blinking' : ''}`;
      const halo = L.polyline(segment, {color, weight: 7, opacity: 0.28, lineCap: 'round', className}).addTo(map);
      const dash = L.polyline(segment, {color, weight: 4, dashArray: '8 8', lineCap: 'round', className}).addTo(map);
      this.mapLayers.push(halo, dash, this.arrowhead(edge, color).addTo(map));
      for (const node of [edge.from, edge.to]) {
        legsOfNode.set(node, [...(legsOfNode.get(node) ?? []), halo, dash]);
      }
    }
    nodes.forEach((node) => {
      const classes = `supply-chain-marker kind-${node.kind}${node.current ? ' is-current is-blinking' : ''}`;
      // The current pin is drawn larger, so its box and anchor grow with it.
      const size = node.current ? 40 : 34;
      const count = node.kind === 'CHECKPOINT' ? 0 : node.records.length;
      const badge = count > 1 ? `<i class="supply-chain-marker-count">${count}</i>` : '';
      const marker = L.marker([node.latitude, node.longitude], {
        icon: L.divIcon({
          className: 'supply-chain-marker-wrapper',
          html: `<span class="${classes}"><span>${node.order}</span></span>${badge}`,
          iconSize: [size, size],
          iconAnchor: [size / 2, size]
        })
      })
        .addTo(map)
        .bindPopup(this.nodePopup(node))
        .on('click', () => this.blink(legsOfNode.get(node) ?? []));
      this.mapLayers.push(marker);
    });
    map.fitBounds(this.routeBounds(nodes), {
      padding: [48, 48],
      maxZoom: nodes.length === 1 ? 12 : 19
    });
  }

  /**
   * Restarts the blink animation on the given legs (a pin was clicked): the class is taken off,
   * the style recalculated, and put back, which is what makes a finite CSS animation run again.
   */
  private blink(legs: L.Polyline[]): void {
    const elements = legs
      .map((leg) => leg.getElement() as SVGElement | undefined)
      .filter((element): element is SVGElement => !!element);
    for (const element of elements) {
      element.classList.remove('is-blinking');
    }
    // Reading a layout property flushes the class removal before it is added again.
    void elements[0]?.getBoundingClientRect();
    for (const element of elements) {
      element.classList.add('is-blinking');
    }
  }

  /**
   * A small triangle at the receiving end of the arrow, turned to the bearing of the leg. It is a
   * marker (screen-sized), so it stays legible at every zoom, and it carries the quantity as a
   * tooltip.
   */
  private arrowhead(edge: MapEdge, color: string): L.Marker {
    const bearing = this.bearing(edge.from, edge.to);
    // Sit the head short of the pin so the pin's own icon never covers it.
    const t = 0.82;
    const latitude = edge.from.latitude + (edge.to.latitude - edge.from.latitude) * t;
    const longitude = edge.from.longitude + (edge.to.longitude - edge.from.longitude) * t;
    const legend = edge.count > 1
      ? `${edge.count} รายการ · ${formatQuantityWithUnit(edge.quantity, DEFAULT_TRADE_UNIT)}`
      : formatQuantityWithUnit(edge.quantity, DEFAULT_TRADE_UNIT);
    return L.marker([latitude, longitude], {
      interactive: false,
      icon: L.divIcon({
        className: 'supply-chain-arrow-wrapper',
        html: `<span class="supply-chain-arrow" style="transform: rotate(${bearing.toFixed(1)}deg); color: ${color}"`
          + ` title="${escapeHtml(legend)}"><svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">`
          + `<path d="M10 1 L19 19 L10 14 L1 19 Z" fill="currentColor" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg></span>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })
    });
  }

  /** Compass bearing from one pin to another in degrees clockwise from north (the CSS rotation of an up-pointing arrow). */
  private bearing(from: MapNode, to: MapNode): number {
    const toRadians = (degrees: number): number => degrees * Math.PI / 180;
    const deltaLongitude = toRadians(to.longitude - from.longitude);
    const latitudeFrom = toRadians(from.latitude);
    const latitudeTo = toRadians(to.latitude);
    const y = Math.sin(deltaLongitude) * Math.cos(latitudeTo);
    const x = Math.cos(latitudeFrom) * Math.sin(latitudeTo) - Math.sin(latitudeFrom) * Math.cos(latitudeTo) * Math.cos(deltaLongitude);
    return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
  }

  /**
   * The pin's popup: its name, then one line per record sitting on it. Records of different
   * businesses can share a pin (an exporter that pinned itself at its seller, work item 179),
   * so each line names its business when the pin holds more than one, and says why it is there.
   */
  private nodePopup(node: MapNode): string {
    const head = `<strong>${escapeHtml(node.title)}</strong><br>${escapeHtml(node.subtitle)}`;
    if (node.kind === 'CHECKPOINT' || node.records.length <= 1) {
      return head;
    }
    const businesses = new Set(node.records.map((item) => item.businessTitle || item.farmName || ''));
    const lines = node.records.slice(0, 8).map((item) => {
      const business = businesses.size > 1 ? `${item.businessTitle || item.farmName || 'ไม่ระบุธุรกิจ'}: ` : '';
      const quantity = item.quantity == null ? '' : ` · ${formatQuantityWithUnit(item.quantity, item.unit || DEFAULT_TRADE_UNIT)}`;
      const pinnedAtSeller = item.locationFromSeller ? ' (ใช้พิกัดของผู้ขาย)' : '';
      return `<li>${escapeHtml(`${business}${tradeRecordLabel(item)} · ${item.productName ?? ''}${quantity}${pinnedAtSeller}`)}</li>`;
    });
    const more = node.records.length > 8 ? `<li>… อีก ${node.records.length - 8} รายการ</li>` : '';
    return `${head}<ul class="supply-chain-popup-list">${lines.join('')}${more}</ul>`;
  }

  private routeBounds(nodes: MapNode[]): L.LatLngBoundsExpression {
    return L.latLngBounds(nodes.map((node) => L.latLng(node.latitude, node.longitude)));
  }

  /** `lat`/`lon` come back as strings; anything unparsable drops that hop off the map. */
  private parseCoordinate(value: string | null | undefined, limit: number): number | null {
    const raw = `${value ?? ''}`.trim();
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < -limit || parsed > limit) {
      return null;
    }
    return parsed;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character] ?? character);
}
